import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';
import * as Signals from 'signals';

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";

export interface BankDetailsParams {
    Iban: KnockoutObservable<string>;
    Bic: KnockoutObservable<string>;
    ValidationFields: KnockoutObservable<any>[];
    UpdateSignal: Signals.Signal;
    DirectDebitInitialise: Signals.Signal;
    AutoInitialise: boolean;
    IsRequired: KnockoutObservable<boolean>;
}

export class DDSepa {
    customMessages: {
        IBAN: string;
        BIC: string;
        BICMin: string;
        BICMax: string;
        IBANMin: string;
        IBANMax: string;
    };

    iban: KnockoutObservable<string>;
    bic: KnockoutObservable<string>;
    validationFields: KnockoutObservable<any>[];
    isRequired: KnockoutObservable<boolean>;
    autoInitialise: boolean;
    firstLoad: boolean;

    updateSignal: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    constructor(params: BankDetailsParams) {
        this.iban = params.Iban;
        this.bic = params.Bic;
        this.updateSignal = params.UpdateSignal;
        this.validationFields = params.ValidationFields;
        this.directDebitInitialise = params.DirectDebitInitialise;
        this.autoInitialise = params.AutoInitialise === undefined ? true : params.AutoInitialise;
        this.isRequired = params.IsRequired;

        this.firstLoad = true;

        if (this.autoInitialise) {
            this.initialise();
        }
        else {
            this.directDebitInitialise.add(this.initialiseSignalMethod);
        }

        this.retrieveCustomMessages();
    }

    private initialiseSignalMethod = (enabled: boolean): void => {
        if (enabled) {
            this.initialise();
        }
        else {
            if (!this.firstLoad) {
                this.dispose();
            }
        }
    };

    private retrieveCustomMessages() {
        this.customMessages = {
            IBAN: localisationHelper.getString("GenericResources", "DD_IBAN"),
            IBANMin: localisationHelper.getString("Validation", "IBANMin"),
            IBANMax: localisationHelper.getString("Validation", "IBANMax"),
            BIC: localisationHelper.getString("GenericResources", "DD_BIC"),
            BICMin: localisationHelper.getString("Validation", "BicMin"),
            BICMax: localisationHelper.getString("Validation", "BicMax")
        }
    }

    private setupValidationFields(): void {
        ValidationService.AddValidationRule(this.iban, this.validationFields, {
            minLength: {
                params: 15,
                message: this.customMessages.IBANMin
            },
            maxLength: {
                params: 34,
                message: this.customMessages.IBANMax
            }
        });

        ValidationService.AddValidationRule(this.bic, this.validationFields, {
            minLength: {
                params: 8,
                message: this.customMessages.BICMin
            },
            maxLength: {
                params: 11,
                message: this.customMessages.BICMax
            }
        });

        ValidationService.HideAllMessages(this.validationFields);
    }

    private removeValidationFields(): void {
        ValidationService.RemoveValidationRule(this.iban, this.validationFields);
        ValidationService.RemoveValidationRule(this.bic, this.validationFields);
    }

    // These subscriptions need to be created after the validation so that
    // the validation can run before the subscription is fired. If not the knockout
    // validation will still be validating on the previous value
    private setupSubscriptions(): void {
        this.iban.subscribe(() => {
            this.updateSignal.dispatch();
        });

        this.bic.subscribe(() => {
            this.updateSignal.dispatch();
        });
    }

    private initialise(): void{
        this.setupValidationFields();
        this.setupSubscriptions();
        this.firstLoad = false;
    }

    public dispose(): void {
        this.removeValidationFields();
        this.directDebitInitialise.remove(this.initialiseSignalMethod);
    }
}

const componentName = 'dd-sepa';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DDSepa,
    template: { require: `text!/Themes/Default/Content/ts/Shared/DirectDebitDetails/${componentName}.html` }
});